import React from 'react'
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent,
} from '@mui/material'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import type { LabelValue } from '../../types/gotypes'

/**
 * The reserved sentinel campaign ids. They are REAL, selectable values rather
 * than absences — a record whose campaign is unknown must still be countable and
 * filterable, which is the whole reason the column is `NOT NULL` with a sentinel
 * default (see goapi/pkg/autocoder/campaign).
 */
export const CAMPAIGN_NONE = 'none'
export const CAMPAIGN_UNKNOWN = 'unknown'

/** How each sentinel reads to a human, who should never see the raw token. */
const SENTINEL_LABELS: Record<string, string> = {
  [CAMPAIGN_NONE]: 'No campaign',
  [CAMPAIGN_UNKNOWN]: 'Unattributed',
}

/**
 * Resolve the text shown for one campaign option.
 *
 * The id is opaque ("2531_13"), so the server sends a display name alongside it
 * and this prefers that. Sentinels are relabelled into English: "unknown" is a
 * storage token, and putting it in front of a user invites the reading "a
 * campaign called unknown".
 */
export function campaignOptionLabel(option: LabelValue): string {
  if (SENTINEL_LABELS[option.value]) return SENTINEL_LABELS[option.value]
  return option.label?.trim() || option.value
}

/**
 * The one-line explanation shown under a sentinel option, or `undefined` for a
 * real campaign (which explains itself).
 *
 * 🔴 THE TWO SENTINELS MEAN DIFFERENT THINGS AND ARE NEVER COLLAPSED (§4 of the
 * plan forbids it). The distinction is genuinely easy to lose, and was lost
 * once already in this epic's own notes:
 *
 *   - `none`    — the record HAS a parent video; Sprinklr handed it to us with
 *                 no campaign tag on it.
 *   - `unknown` — there is no campaign on the parent entity to inherit from.
 *
 * WHY THE COUNT IS A PARAMETER. Measured on bprsky/tiktok 2026-08-14, `none` is
 * EMPTY — 0 records, 0 entities — while `unknown` carries 39,075 records across
 * 480 entities. A zero-count option sitting in a list with no explanation is
 * exactly what reads as a broken filter rather than an empty category, so an
 * empty sentinel says out loud that it is empty. The note disappears on its own
 * the day the bucket fills; nothing has to be remembered or re-edited.
 */
export function sentinelDescription(value: string, count: number): string | undefined {
  if (value === CAMPAIGN_NONE) {
    return count === 0
      ? 'Sprinklr sent these without a campaign tag — none so far'
      : 'Sprinklr sent these without a campaign tag'
  }
  if (value === CAMPAIGN_UNKNOWN) {
    return count === 0
      ? 'The parent video carries no campaign — none so far'
      : 'The parent video carries no campaign'
  }
  return undefined
}

/** True when this option is one of the reserved values rather than a campaign. */
export function isSentinelCampaign(value: string): boolean {
  return value === CAMPAIGN_NONE || value === CAMPAIGN_UNKNOWN
}

/**
 * Order options for the picker: real campaigns first (largest first), sentinels
 * last however big they are.
 *
 * `Unattributed` is frequently one of the biggest buckets — it counts every
 * video we could not key — and letting size float it to the top would put a
 * data-quality artefact where the user looks for their biggest campaign.
 */
export function sortCampaignOptions(options: LabelValue[]): LabelValue[] {
  return [...options].sort((a, b) => {
    const aSentinel = isSentinelCampaign(a.value)
    const bSentinel = isSentinelCampaign(b.value)
    if (aSentinel !== bSentinel) return aSentinel ? 1 : -1
    if (b.count !== a.count) return b.count - a.count
    return campaignOptionLabel(a).localeCompare(campaignOptionLabel(b))
  })
}

export interface CampaignFilterProps {
  /** Campaign counts straight from `/entities/filter-summary` (unfiltered). */
  campaigns: LabelValue[]
  /** Selected campaign ids. Empty means NO constraint — every campaign. */
  selected: string[]
  onChange: (campaigns: string[]) => void
  disabled?: boolean
}

/**
 * Filter the dashboard by Sprinklr campaign.
 *
 * A multi-select rather than the toggle row the platforms use: Sky alone has
 * eleven campaigns with names like "EB&A - Corporate & Consumer (Sky CA)", and
 * a toggle per campaign would wrap the toolbar into several lines before it
 * showed anything useful.
 *
 * Empty selection means "every campaign", matching every other filter on this
 * page — so the control opens neutral and the user's first click narrows rather
 * than having to deselect ten things first.
 */
export const CampaignFilter: React.FC<CampaignFilterProps> = ({
  campaigns,
  selected,
  onChange,
  disabled = false,
}) => {
  const options = React.useMemo(() => sortCampaignOptions(campaigns), [campaigns])

  // A job with one campaign (or none) has nothing to choose between, and a
  // control whose every setting produces the same numbers is just noise.
  if (options.length < 2) return null

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value
    onChange(typeof value === 'string' ? value.split(',') : value)
  }

  const labelFor = (id: string): string => {
    const match = options.find((o) => o.value === id)
    return match ? campaignOptionLabel(match) : id
  }

  return (
    <Box
      data-testid="autocoder-social-campaign-filter"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <LocalOfferIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Campaign
        </Typography>
      </Box>

      <FormControl size="small" sx={{ minWidth: 220, maxWidth: 360 }}>
        <Select<string[]>
          multiple
          displayEmpty
          disabled={disabled}
          value={selected}
          onChange={handleChange}
          data-testid="autocoder-social-campaign-select"
          inputProps={{ 'aria-label': 'Filter by campaign' }}
          renderValue={(ids) =>
            // Empty is the neutral state and must SAY so. A blank box reads as
            // broken, and "All campaigns" is also the honest description of what
            // the numbers below currently include.
            ids.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                All campaigns
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {ids.map((id) => (
                  <Chip key={id} label={labelFor(id)} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                ))}
              </Box>
            )
          }
          sx={{
            borderRadius: 1.5,
            '& .MuiSelect-select': { py: 0.75 },
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              data-testid={`autocoder-social-campaign-option-${option.value}`}
            >
              <Checkbox checked={selected.includes(option.value)} size="small" />
              <ListItemText
                primary={campaignOptionLabel(option)}
                secondary={sentinelDescription(option.value, option.count)}
              />
              <Chip
                label={option.count.toLocaleString()}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', ml: 1 }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

export default CampaignFilter
